import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useQuery, useApolloClient } from '@apollo/client';
import { Col } from 'react-bootstrap';
import { graphqlErrorsToUiErrors, queries, queriesV2, useTeamPermissions } from '../../api';
import { getDealById } from '../../api/queries';
import { useTeamInfo } from '../../team-id-context';
import { useDealId } from '../../deal-id-context';
import ErrorMessage from '../error-message';
import LoadingSpinner from '../loading-spinner';
import { TeamMembersListContainer } from '../teamMembersList';
import { HandleTeamActionsEnum } from '../../pages/deals/constants/AddOrEditTeamConstants';
import SelectTeamMembers from '../../forms/components/SelectTeamMembers';
import { Permissions } from '../../shared/constants/permissions';
import { InviteDealTeamSlideout } from '../invite-deal-team-slideout';
import { NotFound } from '../../pages';
import { useDocumentTeamPermissions } from '../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { User } from '../document-wizard/document-editor/DocumentEditorTypes';
import { useAuth, usePermissions } from '../../auth';
import { Permission } from '../../gql/graphql';
import { TitleFormattedMessage } from '../titleFormattedMessage';
import styles from './styles.module.scss';
import classNames from 'classnames';

const EditTeam = () => {
  const { teamId } = useTeamInfo();
  const [{ authLoading, user }] = useAuth();
  const apolloClient = useApolloClient();
  const dealId = useDealId();
  const [showAddMember, setShowAddMember] = useState(false);
  const { isAdmin } = usePermissions();
  const { loading: permissionsLoading, teamPermissions } = useTeamPermissions();
  const { hasInviteSendingPermission } = useDocumentTeamPermissions({ teamId, apolloClient });

  const {
    loading: getDealByIdDataLoading,
    data,
    error: graphqlError,
  } = useQuery(getDealById, {
    fetchPolicy: 'no-cache',
    variables: {
      dealId,
    },
  });

  const { loading: teamQueryDataLoading, data: teamData } = useQuery(queries.getTeamById, {
    variables: {
      teamId,
    },
  });

  const { loading: dealTeamsQueryLoading, data: dealTeamsData } = useQuery(queriesV2.getDealTeamsByTeamId, {
    variables: {
      dealId,
    },
  });

  const loading =
    getDealByIdDataLoading || teamQueryDataLoading || dealTeamsQueryLoading || permissionsLoading || authLoading;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!data) {
    const errors = graphqlErrorsToUiErrors(graphqlError);
    if (errors._general.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  const viewPermission = teamPermissions?.find(({ key }: any) => key === Permissions.View);
  
  // Deduplicate team members by user.id to prevent duplicate key warnings
  const uniqueTeamMembersMap = new Map();
  teamData.teamById.teamMembers.forEach((member: { user: User }) => {
    if ((isAdmin || member.user.id !== user.id) && !uniqueTeamMembersMap.has(member.user.id)) {
      uniqueTeamMembersMap.set(member.user.id, member);
    }
  });
  const teamMembers = Array.from(uniqueTeamMembersMap.values());
  // The viewer's own side of the deal. `members` is both sides, which listed the
  // counterparty as an active member of the creator's deal team.
  const {
    getDealById: { sameSideMembers: dealMembers },
  } = dealTeamsData;

  return (
    <>
      <Container fluid="md" className="pb-md-3 d-flex flex-column h-100">
        {hasInviteSendingPermission && (
          <Row className={classNames('flex-shrink-0', styles.topControlsRow)}>
            <Col lg={8} md={6} xs={6} className={classNames(styles.existingMember, 'd-flex')}>
              <SelectTeamMembers
                teamMembers={teamMembers}
                dealMembers={dealMembers}
                dealId={dealId}
                viewPermission={viewPermission as Pick<Permission, 'id'>}
              />
            </Col>
            <Col lg={4} md={6} xs={6} className={classNames('text-right', styles.InviteOuterBtn)}>
              <Button
                variant="primary"
                onClick={() => setShowAddMember(true)}
                className={classNames(styles.handleParticipantsButton, styles.inviteBtn)}
              >
                <TitleFormattedMessage
                  id="cta.add-team-member-to-deal"
                  defaultMessage={HandleTeamActionsEnum.INVITE_NEW_MEMBERS}
                />
              </Button>
            </Col>
          </Row>
        )}
        <Row className={classNames('flex-1 min-h-0', styles.heightMobile, styles.scrollableRow)}>
          <Col className={classNames(styles.teamMembersListContainer, styles.contentMemberList)}>
            <TeamMembersListContainer showDealMembersOnly passedTeamData={teamData} />
          </Col>
        </Row>
      </Container>

      <InviteDealTeamSlideout isOpen={showAddMember} onClose={() => setShowAddMember(false)} />
    </>
  );
};

export default EditTeam;
